import React, { useCallback, useEffect, useState } from 'react';
import { organisationsAPI } from '../../../../organisations/api/organisationsAPI';
import { isForbidden } from '../../../../organisations/lib/errorMessage';
import { ExhibitLightboxProvider } from '../../../../quiz-generation/components/examRender';
import EmptyState from '../../common/EmptyState';
import { dayMonthYear } from './lib/activityFormat';
import { markedCount } from './lib/paperReview';
import {
  APP_QUIZ_NOTE, olderQuizNote, quizReviewKind, reviewFromQuiz,
} from './lib/quizReview';
import { PartBody, SummaryTiles } from './PaperReview';
import { quizTitle } from './QuizSection';

const QUIZ_FOOTNOTE = 'Questions, answers and worked solutions were written by the quiz generator from the student’s own files. They have not been checked.';

const failure = (err) => {
  if (err?.status === 404) return { status: 'not_found' };
  if (isForbidden(err)) return { status: 'forbidden' };
  return { status: 'error' };
};

/** The review, loaded once per session id (and again on Retry). */
function useQuizReview(studentId, sessionId) {
  const [state, setState] = useState({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    organisationsAPI.getStudentQuizReview(studentId, sessionId)
      .then((data) => { if (!cancelled) setState({ status: 'ready', data }); })
      .catch((err) => { if (!cancelled) setState(failure(err)); });
    return () => { cancelled = true; };
  }, [studentId, sessionId, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { state, retry };
}

const ReviewHeader = ({ quiz }) => {
  const sub = [
    dayMonthYear(quiz.completed_at || quiz.started_at),
    quiz.completed_at ? null : 'not finished',
  ].filter(Boolean).join(' · ');
  return (
    <div className="td-pr-top">
      <span className="td-eyebrow">{quiz.source === 'app' ? 'Quiz generator · pathbuddy.app' : 'Quiz generator'}</span>
      <h2 className="td-pr-title">{quizTitle(quiz)}</h2>
      {sub ? <p className="td-act-meta">{sub}</p> : null}
    </div>
  );
};

/** A pathbuddy.app quiz: its totals and why there is nothing more. */
const AppTotals = ({ review }) => (
  <>
    <p className="td-act-note td-pr-notice">{APP_QUIZ_NOTE}</p>
    {markedCount(review.summary) > 0 ? <SummaryTiles summary={review.summary} /> : null}
  </>
);

const QuizBody = ({ data, savedSince }) => {
  const kind = quizReviewKind(data.quiz);
  const review = reviewFromQuiz(data);
  if (kind === 'app') return <AppTotals review={review} />;
  const note = kind === 'older' ? (
    <p className="td-act-note td-pr-notice">{olderQuizNote(savedSince)}</p>
  ) : null;
  // A quiz with no question rows at all (an older one whose answers were
  // never recorded one by one): its totals only.
  if (!review.questions.length) {
    return (
      <>
        {note}
        {markedCount(review.summary) > 0 ? <SummaryTiles summary={review.summary} /> : null}
        <p className="td-act-meta">No per-question results were recorded for this quiz.</p>
      </>
    );
  }
  return (
    <>
      {note}
      <PartBody review={review} footnote={kind === 'older' ? null : QUIZ_FOOTNOTE} />
    </>
  );
};

/**
 * One custom quiz question by question, for staff, opened from a Quiz
 * generator row with ?quiz=<session_id>. It reuses the paper review's
 * body and cards through lib/quizReview's adapter, so the generated text
 * goes through the same renderer and sanitiser as a paper's. A quiz made
 * before questions were saved shows each question's topic and result; a
 * pathbuddy.app quiz shows its totals. `onBack` returns to Activity.
 */
const QuizReview = ({
  studentId, studentName, sessionId, onBack,
}) => {
  const { state, retry } = useQuizReview(studentId, sessionId);

  let body = null;
  if (state.status === 'loading') {
    body = <p className="td-loading" role="status">Loading quiz…</p>;
  } else if (state.status === 'not_found') {
    body = <EmptyState title="This quiz could not be found" body="It may have been removed, or it is not this student’s." />;
  } else if (state.status === 'forbidden') {
    body = <EmptyState tone="warning" title="You do not have access to this student." />;
  } else if (state.status === 'error') {
    body = (
      <EmptyState
        tone="warning"
        title="Could not load this quiz"
        action={<button type="button" className="td-btn td-btn--small td-act-retry" onClick={retry}>Retry</button>}
      />
    );
  } else {
    body = (
      <>
        <ReviewHeader quiz={state.data.quiz} />
        <QuizBody key={sessionId} data={state.data} savedSince={state.data.questions_saved_since ?? null} />
      </>
    );
  }

  return (
    <ExhibitLightboxProvider>
      <section className="td-act td-pr" aria-label="Quiz review">
        <button type="button" className="td-btn td-btn--ghost td-profile__back" onClick={onBack}>
          {`← ${studentName || 'Student'} · Activity`}
        </button>
        {body}
      </section>
    </ExhibitLightboxProvider>
  );
};

export default QuizReview;
